/* global SearchEnabled, DebugPanelEnabled, EpgEnabled */

import React from 'react';
import { Route, Redirect, IndexRoute } from 'react-router';
import App from 'app'; // eslint-disable-line
import { isNationEntitled, mvpdDeeplinkEnabled, watchV2Enabled } from 'helpers/ConfigHelper';
import { userAttributeEvents as optimizelyUserAttributeEvents } from 'helpers/OptimizelyHelper';
import { onRouteChange, helperGetBaseUrlSlug } from 'helpers/RouteHelper';
import { isStaticRender } from 'helpers/ContentHelper';
import { getDesignV2Routes, rootScreenUrl } from './routes.helper';

const keyGenerator = () => {
  let counter = 0;
  return () => {
    counter += 1;
    return `route-key-${counter}`;
  };
};
const routeKeyGen = keyGenerator();

const onHomeLoad = () => optimizelyUserAttributeEvents().home();
const onLiveLoad = () => {
  optimizelyUserAttributeEvents().live();
};

const baseUrlSlug = helperGetBaseUrlSlug();
const appViewRoute = `${baseUrlSlug}/app-view`;
const appRoute = baseUrlSlug || '/';
let baseUrlSlugRedirect = null;
if (baseUrlSlug.length > 0) {
  const redirectRoute = `${baseUrlSlug}/`;
  baseUrlSlugRedirect = <Redirect from="/" to={redirectRoute} />;
}

export default (screens = {}) => {
  const {
    ArticleDetailScreen,
    CollectionScreen,
    ChannelScreen,
    ContentDetailScreen,
    DebugScreen,
    Error500Screen,
    Error404Screen,
    FindScreen,
    GeoBlock,
    LatestEpisodeScreen,
    LiveScreen,
    LoadingScreen,
    MVPDScreen,
    PersonalityScreen,
    SiteMapScreen,
    WatchScreen,
    WatchV2Screen,
  } = screens;

  const watchScreenRoute = watchV2Enabled() ? WatchV2Screen : WatchScreen;

  const searchRoute = SearchEnabled && (
    <Route
      key={routeKeyGen()}
      path="find(/:findCollectionId)(/filter/:findFilterId)"
      component={FindScreen}
    />
  );
  const debugRoute = DebugPanelEnabled && [
    <Route key={routeKeyGen()} path="debug" component={DebugScreen} />,
    <Route key={routeKeyGen()} path="debug/:panelId" component={DebugScreen} />,
  ];
  let nationRoutes = [
    <Route key={routeKeyGen()} path="/geo-block" component={GeoBlock} geolocationBlocked />,
    <Route
      key={routeKeyGen()}
      path="crime"
      component={CollectionScreen}
      onEnter={onHomeLoad}
      screenUrl="screens/home-crime"
    />,
    <Route
      key={routeKeyGen()}
      path="shows"
      component={CollectionScreen}
      onEnter={onHomeLoad}
      screenUrl="screens/home-shows"
    />,
    <Route
      key={routeKeyGen()}
      path="personalities"
      component={CollectionScreen}
      onEnter={onHomeLoad}
      screenUrl="screens/home-personalities"
    />,
    <Route
      key={routeKeyGen()}
      path="welcome"
      component={CollectionScreen}
      onEnter={onHomeLoad}
      screenUrl="screens/home-welcome"
    />,
    <Route
      key={routeKeyGen()}
      path="faith"
      component={CollectionScreen}
      onEnter={onHomeLoad}
      screenUrl="screens/home-faith"
    />,
  ];

  const channelsRoute = [
    <Route
      key={routeKeyGen()}
      path="channel/:channelId"
      component={ChannelScreen}
      params={{ fetchFromCategories: true }}
    />,
    <Route
      key={routeKeyGen()}
      path="channel/:channelId/u/"
      component={ChannelScreen}
      params={{ fetchFromCategories: true }}
    />,
  ];
  const personalityDetailRoutes = [
    <Route key={routeKeyGen()} path="personalities/:showId" component={PersonalityScreen} />,
    <Route
      key={routeKeyGen()}
      path="personalities/:showId/:pageId"
      component={PersonalityScreen}
    />,
  ];
  nationRoutes = [...nationRoutes, ...personalityDetailRoutes];
  const defaultRoutes = [];
  let mvpdRoute;
  const RootScreen = CollectionScreen;
  const livePath = EpgEnabled ? 'live(/nav)(/schedule)(/channel/:callSign)' : 'live/:videoId';
  const liveRoute = (
    <Route key={routeKeyGen()} path={livePath} component={LiveScreen} onEnter={onLiveLoad} />
  );

  const designV2Routes = getDesignV2Routes();
  if (!EpgEnabled) designV2Routes.push(liveRoute);

  if (mvpdDeeplinkEnabled()) {
    mvpdRoute = <Route key={routeKeyGen()} path="tv-provider" component={MVPDScreen} />;
  }

  const showRoutes = [
    <Route key={routeKeyGen()} path=":showId" component={ContentDetailScreen} />,
    <Route key={routeKeyGen()} path=":showId/:pageType" component={ContentDetailScreen} />,
    <Route key={routeKeyGen()} path=":showId/:pageType/:pageId" component={ContentDetailScreen} />,
    <Route key={routeKeyGen()} path=":showId/u/" component={ContentDetailScreen} />,
    <Route key={routeKeyGen()} path=":showId/:pageType/u/" component={ContentDetailScreen} />,
    <Route
      key={routeKeyGen()}
      path=":showId/:pageType/:pageId/u/"
      component={ContentDetailScreen}
    />,
  ];
  return (
    <Route key={routeKeyGen()} onChange={onRouteChange}>
      <Redirect from="/http://dev.fox-com.dws.la/*" to="/*" />
      <Redirect from="/http://qa.fox-com.dws.la/*" to="/*" />
      {!isStaticRender() && <Redirect from="/primetime-all-the-time/" to="/primetime" />}
      {!isStaticRender() && isNationEntitled() && (
        <Redirect from="/nation-watch-free*" to="/featured" />
      )}
      {baseUrlSlugRedirect}
      <Route key={routeKeyGen()} path={appViewRoute} appView component={App}>
        <IndexRoute component={RootScreen} onEnter={onHomeLoad} />
        <Route key={routeKeyGen()} path="article/:articleId" component={ArticleDetailScreen} />
      </Route>
      <Route key={routeKeyGen()} path={appRoute} component={App}>
        <IndexRoute component={RootScreen} onEnter={onHomeLoad} screenUrl={rootScreenUrl} />
        {designV2Routes}
        <Route key={routeKeyGen()} path="sitemap" component={SiteMapScreen} />
        <Route
          key={routeKeyGen()}
          path="collection/:collectionId"
          component={RootScreen}
          onEnter={onHomeLoad}
          screenUrl={rootScreenUrl}
        />
        {mvpdRoute}
        {searchRoute}
        {defaultRoutes}
        {channelsRoute}
        {nationRoutes}
        <Route
          key={routeKeyGen()}
          path="profile-details(/:network)"
          component={RootScreen}
          screenUrl={rootScreenUrl}
        />
        <Route
          key={routeKeyGen()}
          path="watch/:videoId(/mode/:modeId)"
          component={watchScreenRoute}
        />
        <Route
          key={routeKeyGen()}
          path="watch/:videoId(/mode/:modeId)/u/"
          component={watchScreenRoute}
        />
        <Route
          key={routeKeyGen()}
          path="full-episodes/latest/:showId"
          component={LatestEpisodeScreen}
        />
        <Route key={routeKeyGen()} path="article/:articleId" component={ArticleDetailScreen} />
        <Route key={routeKeyGen()} path="article/:articleId/u/" component={ArticleDetailScreen} />
        <Route key={routeKeyGen()} path="error.html" component={LoadingScreen} />
        <Route key={routeKeyGen()} path="maintenance.html" maintenance component={Error500Screen} />
        {debugRoute}
        {showRoutes}
        <Route key={routeKeyGen()} path="*" component={Error404Screen} />
      </Route>
    </Route>
  );
};
